import { Alert, Anchor, Card, Center, Grid, Group, Loader, Progress, Stack, Table, Text, Title } from '@mantine/core';
import { IconHourglass, IconPlugConnectedX, IconReceipt, IconUserStar } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { isAppUninstalled } from '../../../helpers/domainSuspension';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import PendingSubscriptionChangeAlert from '../../Subscriptions/PendingSubscriptionChangeAlert';
import SubscriptionConfirmationUrlModal from '../../Subscriptions/SubscriptionConfirmationUrlModal';
import SubscriptionCustomizer from '../../Subscriptions/SubscriptionCustomizer';
import SubscriptionPermissionsManager from '../../Subscriptions/SubscriptionPermissionsManager';
import SubscriptionSelector from '../../Subscriptions/SubscriptionSelector';
import SuperAdminArea from '../../SuperAdmin/SuperAdminArea';

const UsageRecord = ({ usageRecord }) => {
  if (!usageRecord) {
    return;
  }

  return (
    <Table.Tr>
      <Table.Td>{usageRecord.reference}</Table.Td>
      <Table.Td>
        {parseFloat(usageRecord.unitPrice || 0).toLocaleString('en-US', {
          style: 'currency',
          currency: usageRecord.currency,
          maximumFractionDigits: 5,
        })}
      </Table.Td>
      <Table.Td>
        {parseFloat(usageRecord.amount || 0).toLocaleString('en-US', {
          maximumFractionDigits: 2,
        })}
      </Table.Td>
      <Table.Td>
        {parseFloat(usageRecord.billableAmount || 0).toLocaleString('en-US', {
          maximumFractionDigits: 2,
        })}
      </Table.Td>
      <Table.Td>
        {parseFloat(usageRecord.totalPrice || 0).toLocaleString('en-US', {
          style: 'currency',
          currency: usageRecord.currency,
        })}
      </Table.Td>
    </Table.Tr>
  );
};

const SettingsTabUsage = ({ account, domain, setDomain, usage, isSuperAdmin }) => {
  const [confirmationUrl, setConfirmationUrl] = useState();
  const [subscriptions, setSubscriptions] = useState();
  const [error, setError] = useState();

  const fetch = useFetch();

  useEffect(() => {
    fetch(`/api/admin/subscriptions`)
      .then(({ subscriptions }) => {
        setSubscriptions(subscriptions);
      })
      .catch((error) => {
        setError(error);
      });
  }, [fetch]);

  if (!usage) {
    return '';
  }

  if (subscriptions === undefined) {
    return <Loader />;
  }

  const selectSubscriptions = (selectedSubscriptions) => {
    return fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/subscriptions/confirmations`, {
      method: 'POST',
      body: JSON.stringify({ selectedSubscriptions }),
    }).then(({ domainSubscriptions, confirmationUrl }) => {
      if (confirmationUrl) {
        setConfirmationUrl(confirmationUrl);
        // The modal shows the link once; keeping the rows the API just created is
        // what leaves it on the page after the modal is dismissed. The endpoint
        // replaces the domain's pending rows, so the previous ones go.
        const returnedIds = new Set((domainSubscriptions ?? []).map((s) => s.id));
        setDomain({
          ...domain,
          domainSubscriptions: [
            ...domain.domainSubscriptions.filter((s) => s.status !== 'pendingConfirmation' && !returnedIds.has(s.id)),
            ...(domainSubscriptions ?? []),
          ],
        });
      } else {
        setDomain({ ...domain, domainSubscriptions: domainSubscriptions ?? [] });
      }
    });
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  // An account on a flat price has every per-domain usage line replaced by one
  // flat fee on its invoice, so the prices and totals below are informational
  // here. Shown to everyone on the domain, not only super admins: it answers
  // "why is my shop billed nothing?" for the merchant too.
  const flatPriceNotice =
    account.flatPriceAmount && account.flatPriceCurrency ? (
      <Alert color="blue" icon={<IconReceipt size={18} />} mb="md">
        Billing for this shop is managed at the account level: a flat fee of{' '}
        {parseFloat(account.flatPriceAmount).toLocaleString('en-US', {
          style: 'currency',
          currency: account.flatPriceCurrency,
        })}{' '}
        per month covers all of the account&apos;s shops. The subscription prices and usage below are not invoiced —
        usage is shown for information only.{' '}
        {isSuperAdmin ? (
          <>
            See{' '}
            <Anchor
              component={Link}
              to={`/accounts/${domain.accountId}/domains/${domain.id}/settings/account?tab=account`}
            >
              account settings
            </Anchor>
            .
          </>
        ) : (
          <>
            Reach out to <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> if you have questions about
            it.
          </>
        )}
      </Alert>
    ) : null;

  const pendingChangeAlert = <PendingSubscriptionChangeAlert domainSubscriptions={domain.domainSubscriptions} />;

  const activeSubscriptions = domain.domainSubscriptions.filter((s) => s.status === 'active');
  const cancelledSubscriptions = domain.domainSubscriptions.filter((s) => s.status === 'inactive');

  // "No active plan" has two very different causes and they used to share one
  // screen. A domain that never had one is on a bespoke arrangement; a domain
  // whose plans are all 'inactive' had one and it was cancelled - telling that
  // merchant to "contact your account manager for details" about a subscription
  // they ended themselves, and naming a plan they are no longer on, is the
  // wrong answer to a question they did not ask.
  if (activeSubscriptions.length === 0 && cancelledSubscriptions.length > 0) {
    // Only a positively-recorded Shopify uninstall gets told reinstalling brings
    // the plan back - a non-Shopify domain whose only plan was retired by a super
    // admin has no app to reinstall, so it gets a support pointer instead.
    const uninstalled = isAppUninstalled(domain);
    const cancelledPlanNames = cancelledSubscriptions.map((s) => s.appliedValues?.name).filter(Boolean);
    const cancelledPlanNamesText = cancelledPlanNames.length > 0 ? cancelledPlanNames.join(', ') : 'Your plan';
    const wasCancelledText = cancelledPlanNames.length > 1 ? 'were cancelled' : 'was cancelled';

    return (
      <>
        {flatPriceNotice}
        {pendingChangeAlert}
        <Card withBorder>
          <Card.Section p="xl">
            <Center>
              <Stack align="center" gap="md" py="xl">
                <IconPlugConnectedX size={64} stroke={1.5} color="var(--mantine-color-red-6)" />
                <Title order={3} ta="center">
                  Your subscription is no longer active
                </Title>
                <Text c="dimmed" size="lg" ta="center">
                  {cancelledPlanNamesText} {wasCancelledText}, so nothing is being billed and no usage is being
                  recorded.
                </Text>
                <Text c="dimmed" ta="center">
                  {uninstalled
                    ? 'Reinstalling the Releva app in your store brings it back, or reach out to '
                    : 'Reach out to '}
                  <Anchor href="mailto:support@releva.ai">support@releva.ai</Anchor> if you think this is a mistake.
                </Text>
                <SuperAdminArea show={isSuperAdmin} inline>
                  <SubscriptionSelector
                    subscriptions={subscriptions}
                    selectedSubscriptions={[]}
                    onSelect={(subs) => selectSubscriptions(subs)}
                    selectorTitle="Manage subscriptions"
                    size="lg"
                  />
                </SuperAdminArea>
              </Stack>
            </Center>
          </Card.Section>
        </Card>
      </>
    );
  }

  if (activeSubscriptions.length === 0) {
    return (
      <>
        {flatPriceNotice}
        {pendingChangeAlert}
        <Card withBorder>
          <Card.Section p="xl">
            <Center>
              <Stack align="center" gap="md" py="xl">
                <IconUserStar size={64} stroke={1.5} color="var(--mantine-color-violet-5)" />
                <Title order={3} ta="center">
                  You are on a custom subscription
                </Title>
                <Text c="dimmed" size="lg" ta="center">
                  Please contact your account manager for details.
                </Text>
                <SuperAdminArea show={isSuperAdmin} inline>
                  <SubscriptionSelector
                    subscriptions={subscriptions}
                    selectedSubscriptions={activeSubscriptions.map((s) => s.appliedValues)}
                    onSelect={(subs) => selectSubscriptions(subs)}
                    selectorTitle="Manage subscriptions"
                    size="lg"
                  />
                </SuperAdminArea>
              </Stack>
            </Center>
          </Card.Section>
        </Card>
      </>
    );
  }

  const priceCapPercentage = parseInt((usage.totalAmountUSD / usage.priceCapUSD) * 100);

  return (
    <div data-tour="shopPagePlanTabContent">
      {flatPriceNotice}
      {pendingChangeAlert}
      <Card withBorder>
        <Card.Section p="md">
          <Group justify="space-between" mb="md">
            <Title order={3}>{activeSubscriptions.map((d) => d.appliedValues.name).join(', ')}</Title>

            {(domain.account.canManageSubscriptions || isSuperAdmin) && (
              <Group gap="xs">
                <SubscriptionSelector
                  subscriptions={subscriptions}
                  selectedSubscriptions={activeSubscriptions.map((s) => s.appliedValues)}
                  onSelect={(subs) => selectSubscriptions(subs)}
                  selectorTitle="Manage subscriptions"
                />
                <SuperAdminArea show={isSuperAdmin} inline>
                  <SubscriptionCustomizer
                    selectedSubscriptions={activeSubscriptions.map((s) => s.appliedValues)}
                    setSelectedSubscriptions={(subs) => selectSubscriptions(subs)}
                  />
                  <SubscriptionPermissionsManager
                    subscriptions={subscriptions}
                    selectedSubscriptions={activeSubscriptions.map((s) => s.appliedValues)}
                    setSelectedSubscriptions={(updatedSubs) => {
                      const updatedDomainSubscriptions = domain.domainSubscriptions.map((ds) => {
                        const updated = updatedSubs.find((s) => s.id === ds.appliedValues?.id);
                        if (updated) {
                          return {
                            ...ds,
                            appliedValues: { ...ds.appliedValues, allowedActions: updated.allowedActions },
                          };
                        }
                        return ds;
                      });
                      setDomain({ ...domain, domainSubscriptions: updatedDomainSubscriptions });
                    }}
                    accountId={domain.accountId}
                    domainId={domain.id}
                  />
                </SuperAdminArea>
              </Group>
            )}
          </Group>

          <hr />

          <Group justify="space-between" pb="xl">
            {usage.billingPeriod && (
              <Text size="sm" c="dimmed">
                Billing period {new Date(usage.billingPeriod.start).toLocaleString()} -{' '}
                {new Date(usage.billingPeriod.end).toLocaleString()}
              </Text>
            )}
            {usage.remainingTrialDays > 0 && (
              <Text size="sm" c="dimmed">
                {usage.remainingTrialDays} trial days remaining
              </Text>
            )}
          </Group>
          {usage.usageRecords.length > 0 ? (
            <>
              {usage.priceCapUSD > 0 ? (
                <Grid pt="xl" pb="xl">
                  <Grid.Col span={{ base: 12, md: 3 }}></Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }} style={{ textAlign: 'center' }}>
                    <Title order={5}>Usage:</Title>
                    <Progress value={priceCapPercentage} size="xl" />
                    <Text ta="right" c="dimmed" size="sm">
                      {parseFloat(usage.priceCapUSD).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </Text>
                  </Grid.Col>
                </Grid>
              ) : (
                ''
              )}
              <Table striped withTableBorder mt="xl">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Price Component</Table.Th>
                    <Table.Th>Unit Price</Table.Th>
                    <Table.Th>Quantity</Table.Th>
                    <Table.Th>Billable Qty</Table.Th>
                    <Table.Th>Total Price</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {usage.usageRecords.map((usageRecord, i) => {
                    return <UsageRecord usageRecord={usageRecord} key={i} />;
                  })}
                  <Table.Tr>
                    <Table.Td>
                      <strong>Total</strong>
                    </Table.Td>
                    <Table.Td></Table.Td>
                    <Table.Td></Table.Td>
                    <Table.Td></Table.Td>
                    <Table.Td>
                      <strong>
                        {' '}
                        {parseFloat(usage.totalAmount || 0).toLocaleString('en-US', {
                          style: 'currency',
                          currency: account.invoiceCurrency,
                        })}
                      </strong>
                    </Table.Td>
                  </Table.Tr>
                </Table.Tbody>
              </Table>
              {usage.lastUpdatedAt && (
                <Text c="dimmed" size="sm" mt="md">
                  * Last updated: {new Date(usage.lastUpdatedAt).toLocaleString()}
                </Text>
              )}
              <Text c="dimmed" size="sm">
                ** All prices excluding VAT.
              </Text>
              <Text size="sm" c="dimmed">
                *** Your billing period is in the GMT timezone, whereas analytics in the platform are shown in your
                local timezone. Therefore, small discepancies between the numbers here and the numbers shown in
                analytics are to be expected.{' '}
              </Text>
            </>
          ) : (
            <div style={{ padding: '2rem 0', textAlign: 'center' }}>
              <IconHourglass size={32} />
              <Title order={3}>There is no usage information yet.</Title>
              <Title order={4}>Please check back later today or tomorrow.</Title>
            </div>
          )}
        </Card.Section>
      </Card>

      <SubscriptionConfirmationUrlModal confirmationUrl={confirmationUrl} setConfirmationUrl={setConfirmationUrl} />
    </div>
  );
};
export default SettingsTabUsage;
